import React, { useEffect, useState } from "react";
import { AlertTriangle, LogOut, Clock } from "lucide-react";

const MAX_ASSIST_MS = 2 * 60 * 60 * 1000; // 2 hours

export default function AssistModeBanner() {
  const [now, setNow] = useState(Date.now());

  const isAssistMode = localStorage.getItem("assist_mode") === "true";
  const tenantName = localStorage.getItem("assist_tenant_name") || "Tenant";
  const tenantSlug = localStorage.getItem("assist_tenant_slug") || "";
  const accessMode = localStorage.getItem("assist_access_mode") || "full";
  const startedAt = Number(localStorage.getItem("assist_started_at") || Date.now());

  useEffect(() => {
    if (!isAssistMode) return;

    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isAssistMode]);

  if (!isAssistMode) return null;

  const elapsedMs = now - startedAt;
  const remainingMs = Math.max(0, MAX_ASSIST_MS - elapsedMs);

  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };

  const exitAssistMode = async () => {
    const platformAccessToken = localStorage.getItem("platform_return_access_token");
    const platformRefreshToken = localStorage.getItem("platform_return_refresh_token");
    const platformUser = localStorage.getItem("platform_return_user");

    const exitTenantSlug = localStorage.getItem("assist_tenant_slug");
    const exitTenantName = localStorage.getItem("assist_tenant_name");

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");

    localStorage.removeItem("assist_mode");
    localStorage.removeItem("assist_tenant_name");
    localStorage.removeItem("assist_tenant_slug");
    localStorage.removeItem("assist_started_at");
    localStorage.removeItem("assist_access_mode");

    if (platformAccessToken) localStorage.setItem("access_token", platformAccessToken);
    if (platformRefreshToken) localStorage.setItem("refresh_token", platformRefreshToken);
    if (platformUser) localStorage.setItem("user", platformUser);

    localStorage.removeItem("platform_return_access_token");
    localStorage.removeItem("platform_return_refresh_token");
    localStorage.removeItem("platform_return_user");

    try {
      await fetch("/api/platform/assist/exit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${platformAccessToken}`,
        },
        body: JSON.stringify({
          tenant_slug: exitTenantSlug,
          tenant_name: exitTenantName,
        }),
      });
    } catch (err) {
      console.warn("Assist exit audit failed", err);
    }

    window.location.href = "/platform";
  };

  if (remainingMs <= 0) {
    exitAssistMode();
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black shadow-xl border-b-2 border-amber-200">
      <div className="max-w-screen-2xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-8 h-8 rounded-md bg-black/15 shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>

          <div className="min-w-0">
            <div className="text-sm font-black tracking-wide">
              RAPID X SUPPORT MODE
            </div>
            <div className="text-xs font-semibold truncate">
              Assisting {tenantName}
              {accessMode === "read_only" ? " — READ ONLY" : " — FULL ACCESS"}
            </div>
          </div>

          <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold bg-black/10 rounded px-2 py-1 shrink-0">
            <Clock className="w-3 h-3" />
            {formatDuration(remainingMs)} remaining
          </span>

          {tenantSlug && (
            <span className="hidden lg:inline text-xs font-bold bg-black/10 rounded px-2 py-1 shrink-0">
              Tenant: {tenantSlug}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={exitAssistMode}
          className="inline-flex items-center gap-2 rounded-md bg-black text-amber-400 hover:bg-zinc-900 px-4 py-2 text-xs font-black shrink-0"
        >
          <LogOut className="w-4 h-4" />
          Return to Platform
        </button>
      </div>
    </div>
  );
}
